import type { DehydratedState } from "@tanstack/react-query";
import type {
  PersistedClient,
  Persister,
} from "@tanstack/react-query-persist-client";
import {
  clear,
  createStore,
  delMany,
  entries,
  keys,
  setMany,
} from "idb-keyval";

export const PERSIST_MAX_AGE = 1000 * 60 * 60 * 24 * 24;

const idbDbName = `${process.env.NEXT_PUBLIC_BASE_PATH}`.replaceAll(
  /[^a-zA-Z0-9_]/g,
  "",
);

const META_KEY = "meta";
const QUERY_PREFIX = "query:";

interface PersistMeta {
  buster: string;
  timestamp: number;
  mutations: DehydratedState["mutations"];
}

function isQueryKey(key: IDBValidKey): key is string {
  return typeof key === "string" && key.startsWith(QUERY_PREFIX);
}

export function createIndexedDbPersister({
  userId,
}: {
  userId: string;
}): Persister {
  const idbStore = createStore(`${idbDbName}:${userId}`, "query-cache");

  return {
    async persistClient(client: PersistedClient) {
      const { queries, mutations } = client.clientState;

      const queryRecords: [string, unknown][] = queries.map((query) => [
        `${QUERY_PREFIX}${query.queryHash}`,
        query,
      ]);

      const meta: PersistMeta = {
        buster: client.buster,
        timestamp: client.timestamp,
        mutations,
      };

      await setMany([...queryRecords, [META_KEY, meta]], idbStore);

      const liveKeys = new Set(queryRecords.map(([key]) => key));
      const staleKeys = (await keys(idbStore)).filter(
        (key) => isQueryKey(key) && !liveKeys.has(key),
      );
      if (staleKeys.length > 0) {
        await delMany(staleKeys, idbStore);
      }
    },
    async restoreClient() {
      const allEntries = await entries(idbStore);

      const meta: PersistMeta | undefined = allEntries.find(
        ([key]) => key === META_KEY,
      )?.[1];
      if (!meta) {
        return undefined;
      }

      const queries: DehydratedState["queries"] = allEntries
        .filter(([key]) => isQueryKey(key))
        .map(([, value]) => value);

      return {
        buster: meta.buster,
        timestamp: meta.timestamp,
        clientState: { mutations: meta.mutations, queries },
      } satisfies PersistedClient;
    },
    async removeClient() {
      await clear(idbStore);
    },
  };
}
